<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs v-model="activeTab" lazy>
          <b-tab :title="$t('globals.all_items')"></b-tab>
          <b-tab v-if="hasWebshopItems">
            <template #title>
              🛒 {{ $t('globals.webshop_items') }}
            </template>
          </b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <b-button
            variant="primary"
            class="create-btn"
            @click="$router.push({ name: 'newitem' })"
          >
            {{ $t('globals.add_new_item').toUpperCase() }}
          </b-button>
        </div>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">{{ $t("globals.search") }}</label>
              <b-form-input
                v-model="filter"
                class="search-input"
                :placeholder="$t('globals.type_to_search')"
              />
            </div>

            <div class="action-buttons">
              <button
                class="reload-btn"
                @click="resetReload"
                :disabled="loading"
                v-b-tooltip.hover
                :title="$t('globals.clear_filters')"
              >
                <icon name="sync-alt" :spin="loading"></icon>
              </button>
            </div>
          </div>

          <!-- Table -->
          <div class="table-wrapper">
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in paginatedItems"
                  :key="item.id"
                  class="table-row"
                  @click="rowClicked(item)"
                >
                  <td>{{ item.id }}</td>
                  <td>{{ item.brand }}</td>
                  <td>{{ item.fleet_size || '-' }}</td>
                  <td>{{ item.contract_duration || '-' }}</td>
                  <td>
                    <span class="customer-link" style="cursor: pointer;">
                      {{ item.item_name }} <span v-if="item.is_a_webshop_item">🛒</span>
                    </span>
                  </td>
                  <td>{{ item.item_type || '-' }}</td>
                  <td>{{ item.kpi_type || '-' }}</td>
                  <td>{{ item.exact_code ? item.exact_code.description : '-' }}</td>
                  <td style="text-align: right;">
                    {{ currencyFormat(item.price_one_time, item.currency) }}
                  </td>
                  <td style="text-align: right;">
                    {{ currencyFormat(item.price_per_month, item.currency) }}
                  </td>
                  <td style="text-align: right;">
                    {{ currencyFormat(item.costs, item.currency) }}
                  </td>
                  <td>{{ item.vat }}%</td>
                  <td>{{ item.currency }}</td>
                  <td v-if="activeTab === 1" @click.stop>
                    <set-shipping-fee
                      v-if="item.is_a_webshop_item"
                      :item="item"
                      @reloadList="fetchItems"
                    />
                  </td>
                  <td v-if="activeTab === 1">{{ item.webshop_position || '-' }}</td>
                  <td v-if="activeTab === 1" @click.stop>
                    <set-position-item
                      v-if="item.webshop_item"
                      :id="parseInt(item.webshop_item.id, 10)"
                      :position="item.webshop_item.position != null ? parseInt(item.webshop_item.position, 10) : null"
                      @reloadList="fetchItems"
                      :maxPosition="maxWebshopPosition"
                    />
                  </td>
                  <td v-if="activeTab == 0">
                    <span class="status-badge" :class="item.displayed ? 'status-completed' : 'status-cancelled'">
								      {{ item.displayed ? $t("globals.displayed") : $t("globals.not_displayed") }}
							      </span>
                    
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>
            <div v-else-if="paginatedItems.length === 0" class="empty-state">
              <p>{{ $t("globals.no_results") }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.per_page") }}</span>
              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                class="per-page-select"
              />
              <span class="summary-label" style="margin-left: 1rem;">
                {{ $t("globals.showing") }}
                {{ ((currentPage - 1) * perPage) + 1 }}-{{ Math.min(currentPage * perPage, searchFilteredItems.length) }}
                {{ $t("globals.of") }} {{ searchFilteredItems.length }}
              </span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage = 1"
              >
                &lt;&lt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="currentPage--"
              >
                &lt;
              </button>
              <span style="padding: 0 1rem; color: #6c757d; font-size: 0.875rem;">
                {{ $t("globals.page") }} {{ currentPage }} {{ $t("globals.of") }} {{ totalPages }}
              </span>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage++"
              >
                &gt;
              </button>
              <button
                class="page-btn"
                :disabled="currentPage >= totalPages"
                @click="currentPage = totalPages"
              >
                &gt;&gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, mapMutations } from "vuex";
import { currencyFormat } from "@/helpers/price";

import "vue-awesome/icons/sync-alt";

export default {
  components: {
    'set-position-item': () => import('@/components/webshop/set_position_item.vue'),
    'set-shipping-fee': () => import('@/components/webshop/set_shipping_fee.vue'),
    Icon: () => import("vue-awesome/components/Icon.vue"),
  },
  data() {
    return {
      filter: "",
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      perPage: 20,
      loading: false,
      activeTab: 0,
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      items: []
    };
  },
  computed: {
    ...mapGetters(["getDefConf", "getGraphQlUrl"]),
    filteredItems() {
      let items = this.items;
      // activeTab === 1 means webshop items tab is active
      if (this.activeTab === 1) {
        items = items.filter(i => i.is_a_webshop_item);
      }

      // Add numeric webshop_position for sorting
      return items.map(i => ({
        ...i,
        webshop_position: i.webshop_item ? Number(i.webshop_item.position) : null
      }));
    },
    searchFilteredItems() {
      if (!this.filter) {
        return this.sortedItems;
      }
      const searchTerm = this.filter.toLowerCase();
      return this.sortedItems.filter(item => {
        return (
          (item.id && item.id.toString().includes(searchTerm)) ||
          (item.brand && item.brand.toLowerCase().includes(searchTerm)) ||
          (item.fleet_size && item.fleet_size.toString().includes(searchTerm)) ||
          (item.contract_duration && item.contract_duration.toString().includes(searchTerm)) ||
          (item.item_name && item.item_name.toLowerCase().includes(searchTerm)) ||
          (item.item_type && item.item_type.toLowerCase().includes(searchTerm)) ||
          (item.kpi_type && item.kpi_type.toLowerCase().includes(searchTerm)) ||
          (item.exact_code && item.exact_code.description && item.exact_code.description.toLowerCase().includes(searchTerm))
        );
      });
    },
    sortedItems() {
      const items = [...this.filteredItems];
      if (!this.sortBy) {
        return items;
      }

      return items.sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        // Handle nested exact_code.description
        if (this.sortBy === 'exact_code.description') {
          aVal = a.exact_code?.description;
          bVal = b.exact_code?.description;
        }

        if (aVal === bVal) return 0;

        let result = 0;
        if (aVal === null || aVal === undefined) {
          result = 1;
        } else if (bVal === null || bVal === undefined) {
          result = -1;
        } else {
          result = aVal < bVal ? -1 : 1;
        }

        return this.sortDesc ? -result : result;
      });
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.searchFilteredItems.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.searchFilteredItems.length / this.perPage) || 1;
    },
    hasWebshopItems() {
      return this.items.some(item => item.is_a_webshop_item);
    },
    fields() {
      let fields = [
        {
          key: "id",
          label: "ID",
          sortable: true
        },
        {
          key: "brand",
          label: this.$t("globals.brand"),
          sortable: true
        },
        {
          key: "fleet_size",
          label: this.$t("globals.fleet_size"),
          sortable: true
        },
        {
          key: "contract_duration",
          label: this.$t("globals.contr_dur"),
          sortable: true
        },
        {
          key: "item_name",
          label: this.$t("globals.name"),
          sortable: true
        },
        {
          key: "item_type",
          label: this.$t("globals.type"),
          sortable: true
        },
        {
          key: "kpi_type",
          label: "KPI",
          sortable: true
        },
        {
          key: "exact_code.description",
          label: "Exact",
          sortable: true
        },
        {
          key: "price_one_time",
          label: this.$t("globals.price_once"),
          sortable: true
        },
        {
          key: "price_per_month",
          label: this.$t("globals.price_monthly"),
          sortable: true
        },
        {
          key: "costs",
          label: this.$t("globals.costs"),
          sortable: true
        },
        {
          key: "vat",
          label: this.$t("globals.vat"),
          sortable: true
        },
        {
          key: "currency",
          label: this.$t("globals.currency"),
          sortable: true
        },
        
      ];

      // activeTab === 1 means webshop items tab is active
      if(this.activeTab === 1){
        fields.push({
          key: "shipping_fee",
          label: this.$t("globals.shipping_fee"),
          sortable: false
        });
        fields.push({
          key: "webshop_position",
          label: this.$t("webshop.position"),
          sortable: true,
        },{
          key: "webshop_set_position",
          label: this.$t("webshop.set_position"),
          sortable: false
        });
      }
      if(this.activeTab === 0){
        fields.push({
          key: "display",
          label: this.$t("globals.displayed"),
          sortable: false
        });
      }
      return fields;
    },
    maxWebshopPosition(){
      let positions = this.items
        .filter(i => i.is_a_webshop_item && i.webshop_item && i.webshop_item.position != null)
        .map(i => parseInt(i.webshop_item.position, 10));
      return positions.length > 0 ? Math.max(...positions) : 0;
    }
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    currencyFormat,
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
    },
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "item", params: { id: item.id } });
    },
    fetchItems() {
      const data = {
        query: `
          query {
            allPriceList {
              id
              brand
              fleet_size
              contract_duration
              item_name
              item_type
              price_one_time
              price_per_month
              vat
              currency
              costs
              exact_code {
                description
              }
              kpi_type
              is_device
              is_a_webshop_item
              webshop_item{
                id
                position
                is_shipping_fee
              }
              displayed
            }
          }
        `
      };
      this.loading = true;
      axios
        .post(this.getGraphQlUrl, data, this.getDefConf)
        .then(res => {
          if (res.data.errors !== undefined && res.data.errors.length > 0) {
            alert(res.data.errors[0].message);
          } else {
            this.items = res.data.data.allPriceList;
          }
        })
        .catch(err => {
          alert(err);
          console.error(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    resetReload() {
      this.filter = "";
      this.currentPage = 1;
      this.sortBy = "id";
      this.sortDesc = true;
      this.fetchItems();
    }
  },
  watch: {
    filter() {
      this.currentPage = 1;
    },
    perPage() {
      this.currentPage = 1;
    },
    activeTab() {
      this.currentPage = 1;
    }
  },
  created() {
    this.fetchItems();
  }
};
</script>
